import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import posthog from "posthog-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft,
  Calendar as CalendarIcon,
  MapPin,
  Euro,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import {
  APPLICATION_UPLOAD_ACCEPT,
  applicationUploadProblem,
} from "@/lib/application-upload";
import { supabase } from "../lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { AuthModal } from "@/components/auth/AuthModal";
import { CompleteAccountDialog } from "@/components/auth/CompleteAccountDialog";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { computeFirstMonthRentEUR } from "@/lib/rent";
import { applicationDepositEUR } from "@/lib/application-deposit";
import { profileNameFromApplication } from "@/lib/application-profile-name";
import { getMinToDateAfterSixMonths } from "@/lib/booking-dates";
import {
  formatNextAvailableDate,
  getListingAvailability,
  moveInAllowsBooking,
} from "@/lib/roomAvailability";
import { ReferralCodeField } from "@/components/booking/ReferralCodeField";
import { NationalityCombobox } from "@/components/booking/NationalityCombobox";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  normalizeNationality,
  requiresResidencePermit,
} from "@/lib/nationality";
import {
  EMPTY_REFERRAL_CODE_STATE,
  normaliseReferralCode,
  REFERRAL_URL_PARAM,
  referralAttachWarning,
  type ReferralCodeState,
} from "@/lib/referrals";
import room1 from "@/assets/room-1.jpg";
import applyHero from "@/assets/apply-hero.png";
import { getImageUrl } from "@/lib/imageUrl";
import { roomHeroImage } from "@/lib/roomImages";
import { openTenantDocument } from "@/lib/tenant-document-url";
import { operatorStamped } from "@/lib/operator-stamped";

// Fallback translations when Storyblok cache is missing keys (so language switch works)
const BOOKING_FALLBACKS: Record<string, Record<string, string>> = {
  en: {
    "booking.fromDate": "From Date",
    "booking.toDate": "To Date",
    "booking.availableFrom": "Available from",
    "booking.moveInBeforeAvailable":
      "This room is available from {date}. Please choose a move-in date on or after that day.",
    "booking.moveInDatePlaceholder": "Select preferred move-in date",
    "booking.fromDatePlaceholder": "Same as Preferred Move-in Date",
    "booking.fromDateHint": "Same as Preferred Move-in Date (auto-filled).",
    "booking.toDatePlaceholder": "Select date (after 6 months from move-in)",
    "booking.toDateHint":
      "To Date is 6 months after move-in (auto-filled, not editable).",
    "booking.dateRequired": "Preferred Move-in Date and To Date are required.",
    "booking.toDateInvalid": "Invalid date",
    "booking.toDateMinError":
      "To Date must be at least 6 months after the Preferred Move-in Date.",
    "booking.requiredDocuments": "Required documents",
    "booking.documentIdPassport": "Government ID or Passport",
    "booking.documentSalaryProof": "Salary Statement / Guarantee",
    "booking.alreadyUploaded": "Already uploaded",
    "booking.useThisDocument": "Use this document for this application",
    "booking.viewDocument": "View document",
    "booking.chooseFile": "Choose File",
    "booking.noFileChosen": "No file chosen",
    "booking.documentWrongType":
      "Please choose a PDF, JPG, PNG, WebP or HEIC file.",
    "booking.documentTooLarge":
      "This file is larger than 20 MB. Please choose a smaller one.",
    "booking.checkingDocuments": "Checking existing documents...",
    "booking.documentOnFile": "Document on file",
    "booking.successTitle": "Application submitted",
    "booking.success": "Thank you for your application",
    "booking.description": "Tell us about yourself",
    "booking.descriptionPlaceholder":
      "Tell us a bit about yourself, your lifestyle, and why you'd like to live here.",
    "booking.firstMonthProRatedRent": "First month pro-rated rent",
    "booking.monthlyRentThereafter": "Monthly rent thereafter",
    "booking.accountBannerText":
      "Just fill out the form below — we'll create your coliviq account when you submit, so you can track your application and keep your documents on file.",
    "booking.alreadyHaveAccount": "Already have an account?",
    "booking.signIn": "Sign in",
    "booking.dailyLimitTitle": "Daily application limit reached",
    "booking.dailyLimit":
      "You have reached the limit of 3 applications per day. Please try again tomorrow.",
    "booking.submitErrorTitle": "Error",
    "booking.submitError": "Failed to submit booking. Please try again.",
    "booking.firstName": "First name",
    "booking.firstNamePlaceholder": "Enter your first name",
    "booking.lastName": "Last name",
    "booking.lastNamePlaceholder": "Enter your last name",
    "booking.nameRequiredTitle": "Name required",
    "booking.nameRequiredDescription": "Please enter your first and last name.",
    "booking.nationality": "Nationality",
    "booking.nationalityPlaceholder": "Select nationality",
    "booking.nationalitySearch": "Search country",
    "booking.nationalityEmpty": "No country found",
    "booking.nationalityRequiredTitle": "Nationality required",
    "booking.nationalityRequiredDescription": "Please select your nationality.",
    "booking.documentResidencePermit": "Residence permit / visa",
    "booking.residencePermitHint":
      "As a national of a country outside the EU, EEA and Switzerland, you need to provide proof of your right to reside in Germany.",
    "booking.residencePermitRequired":
      "Please upload your residence permit or visa to continue.",
    "booking.accountCreatedPermitFailed":
      "Account created, but your residence permit could not be uploaded. Confirm your email and send the permit to kontakt@coliviq.de.",
    "booking.jobcenterQuestion": "Does the Jobcenter pay your rent?",
    "booking.jobcenterYes": "Yes",
    "booking.jobcenterNo": "No",
    "booking.jobcenterRequiredTitle": "Answer required",
    "booking.jobcenterRequiredDescription":
      "Please tell us whether the Jobcenter pays your rent.",
  },
  de: {
    "booking.fromDate": "Von Datum",
    "booking.toDate": "Bis Datum",
    "booking.availableFrom": "Verfügbar ab",
    "booking.moveInBeforeAvailable":
      "Dieses Zimmer ist ab {date} verfügbar. Bitte wähle ein Einzugsdatum ab diesem Tag.",
    "booking.moveInDatePlaceholder": "Bevorzugtes Einzugsdatum wählen",
    "booking.fromDatePlaceholder": "Gleich wie bevorzugtes Einzugsdatum",
    "booking.fromDateHint":
      "Gleich wie bevorzugtes Einzugsdatum (automatisch ausgefüllt).",
    "booking.toDatePlaceholder": "Datum wählen (6 Monate nach Einzug)",
    "booking.toDateHint":
      "Bis Datum ist 6 Monate nach Einzug (automatisch, nicht änderbar).",
    "booking.dateRequired":
      "Bevorzugtes Einzugsdatum und Bis Datum sind erforderlich.",
    "booking.toDateInvalid": "Ungültiges Datum",
    "booking.toDateMinError":
      "Bis Datum muss mindestens 6 Monate nach dem bevorzugten Einzugsdatum liegen.",
    "booking.requiredDocuments": "Erforderliche Unterlagen",
    "booking.documentIdPassport": "Personalausweis oder Reisepass",
    "booking.documentSalaryProof": "Gehaltsnachweis / Bürgschaft",
    "booking.alreadyUploaded": "Bereits hochgeladen",
    "booking.useThisDocument": "Dieses Dokument für diese Bewerbung verwenden",
    "booking.viewDocument": "Dokument ansehen",
    "booking.chooseFile": "Datei wählen",
    "booking.noFileChosen": "Keine Datei gewählt",
    "booking.documentWrongType":
      "Bitte wähle eine PDF-, JPG-, PNG-, WebP- oder HEIC-Datei.",
    "booking.documentTooLarge":
      "Diese Datei ist größer als 20 MB. Bitte wähle eine kleinere Datei.",
    "booking.checkingDocuments": "Vorhandene Dokumente werden geprüft...",
    "booking.documentOnFile": "Dokument vorhanden",
    "booking.successTitle": "Bewerbung eingereicht",
    "booking.success": "Vielen Dank für deine Bewerbung",
    "booking.description": "Erzähl uns von dir",
    "booking.descriptionPlaceholder":
      "Erzähl uns ein wenig über dich, deinen Lebensstil und warum du hier wohnen möchtest.",
    "booking.firstMonthProRatedRent": "Anteilige Miete im ersten Monat",
    "booking.monthlyRentThereafter": "Reguläre Monatsmiete danach",
    "booking.accountBannerText":
      "Füll einfach das untenstehende Formular aus – wir erstellen bei der Übermittlung dein coliviq-Konto, damit du deine Bewerbung verfolgen und deine Dokumente speichern kannst.",
    "booking.alreadyHaveAccount": "Du hast bereits ein Konto?",
    "booking.signIn": "Anmelden",
    "booking.dailyLimitTitle": "Tägliches Bewerbungslimit erreicht",
    "booking.dailyLimit":
      "Du hast das Limit von 3 Bewerbungen pro Tag erreicht. Bitte versuche es morgen erneut.",
    "booking.submitErrorTitle": "Fehler",
    "booking.submitError":
      "Bewerbung konnte nicht übermittelt werden. Bitte versuche es erneut.",
    "booking.firstName": "Vorname",
    "booking.firstNamePlaceholder": "Gib deinen Vornamen ein",
    "booking.lastName": "Nachname",
    "booking.lastNamePlaceholder": "Gib deinen Nachnamen ein",
    "booking.nameRequiredTitle": "Name erforderlich",
    "booking.nameRequiredDescription":
      "Bitte gib deinen Vor- und Nachnamen ein.",
    "booking.nationality": "Staatsangehörigkeit",
    "booking.nationalityPlaceholder": "Staatsangehörigkeit wählen",
    "booking.nationalitySearch": "Land suchen",
    "booking.nationalityEmpty": "Kein Land gefunden",
    "booking.nationalityRequiredTitle": "Staatsangehörigkeit erforderlich",
    "booking.nationalityRequiredDescription":
      "Bitte wähle deine Staatsangehörigkeit aus.",
    "booking.documentResidencePermit": "Aufenthaltstitel / Visum",
    "booking.residencePermitHint":
      "Als Staatsangehörige:r außerhalb von EU, EWR und Schweiz brauchst du einen Nachweis über dein Aufenthaltsrecht in Deutschland.",
    "booking.residencePermitRequired":
      "Bitte lade deinen Aufenthaltstitel oder dein Visum hoch, um fortzufahren.",
    "booking.accountCreatedPermitFailed":
      "Konto erstellt, aber dein Aufenthaltstitel konnte nicht hochgeladen werden. Bestätige deine E-Mail und sende ihn an kontakt@coliviq.de.",
    "booking.jobcenterQuestion": "Zahlt das Jobcenter deine Miete?",
    "booking.jobcenterYes": "Ja",
    "booking.jobcenterNo": "Nein",
    "booking.jobcenterRequiredTitle": "Antwort erforderlich",
    "booking.jobcenterRequiredDescription":
      "Bitte gib an, ob das Jobcenter deine Miete zahlt.",
  },
};

interface ExistingDocument {
  file_name?: string | null;
  file_url?: string | null;
}

interface ApplicationDocumentFieldProps {
  id: string;
  label: string;
  /** Explains why the document is asked for, under the label. */
  hint?: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  inputRef: React.RefObject<HTMLInputElement>;
  /** The applicant's latest document of this type, when they have one on file. */
  existing: ExistingDocument | null;
  useExisting: boolean;
  onUseExistingChange: (use: boolean) => void;
  loading: boolean;
  translate: (key: string) => string;
  onView: (fileUrl: string) => void;
  /** A chosen file the server would refuse: wrong format or over the size limit. */
  onFileRejected: (message: string) => void;
}

/**
 * One row of "Required documents": label, the document already on file (with a
 * switch to reuse it), and the "Choose file" button beside a read-only
 * file-name box. ID, residence permit and salary proof all render through this,
 * so a fix to one is a fix to all three.
 */
const ApplicationDocumentField = ({
  id,
  label,
  hint,
  file,
  onFileChange,
  inputRef,
  existing,
  useExisting,
  onUseExistingChange,
  loading,
  translate,
  onView,
  onFileRejected,
}: ApplicationDocumentFieldProps) => {
  const reusing = !!existing && useExisting;
  return (
    <div>
      <Label htmlFor={id} className="block text-sm text-muted-foreground mb-2">
        {label}
      </Label>
      {hint && <p className="mb-2 text-xs text-muted-foreground">{hint}</p>}
      {loading ? (
        <p className="text-xs text-muted-foreground mb-2">
          {translate("booking.checkingDocuments")}
        </p>
      ) : existing ? (
        <div className="mb-3 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground space-y-2">
          <p className="font-medium text-foreground">
            {translate("booking.alreadyUploaded")}
          </p>
          <p>{existing.file_name || translate("booking.documentOnFile")}</p>
          <label className="flex items-center gap-2 text-xs text-foreground">
            <input
              type="checkbox"
              checked={useExisting}
              onChange={(e) => onUseExistingChange(e.target.checked)}
            />
            {translate("booking.useThisDocument")}
          </label>
          {existing.file_url && (
            <button
              type="button"
              onClick={() => onView(existing.file_url as string)}
              className="text-primary underline"
            >
              {translate("booking.viewDocument")}
            </button>
          )}
        </div>
      ) : null}
      <div className="flex gap-2 items-center">
        <input
          ref={inputRef}
          id={id}
          type="file"
          accept={APPLICATION_UPLOAD_ACCEPT}
          disabled={reusing}
          className="hidden"
          onChange={(e) => {
            const chosen = e.target.files?.[0] || null;
            const problem = chosen ? applicationUploadProblem(chosen) : null;
            if (problem) {
              // Clear the input so choosing the same file again still fires.
              e.target.value = "";
              onFileRejected(
                translate(
                  problem === "size"
                    ? "booking.documentTooLarge"
                    : "booking.documentWrongType"
                )
              );
              return;
            }
            onFileChange(chosen);
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={reusing}
          onClick={() => inputRef.current?.click()}
        >
          {translate("booking.chooseFile")}
        </Button>
        <Input
          readOnly
          className="flex-1 bg-muted/50 cursor-default"
          value={file?.name ?? ""}
          placeholder={translate("booking.noFileChosen")}
        />
      </div>
    </div>
  );
};

const BookingConfirmation = () => {
  const { t, language } = useLanguage();
  const queryClient = useQueryClient();
  const numberLocale = language === "de" ? "de-DE" : "en-US";
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomIdParam = searchParams.get("roomId");
  const propertyIdParam = searchParams.get("propertyId");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  // COL-246: account-creation dialog shown to logged-out applicants on submit.
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountSubmitting, setAccountSubmitting] = useState(false);
  const [accountError, setAccountError] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<number | null>(null);
  const [room, setRoom] = useState<{
    id: number;
    title: string;
    address: string;
    price: number;
    /** Deposit set on the room in admin (rooms.deposit_amount); null when none. */
    baseDeposit: number | null;
    image: string;
    /** The room's own display image (first of rooms.images); null when it has none. */
    heroImage: string | null;
    /**
     * Confirmed move-out date the room frees up on, when that day is still
     * ahead; null when the room is free today. The listing page hides Apply
     * until this date arrives, but the referral dashboard links straight here
     * (roomApplyUrl) — so the form enforces the same date itself (COL-434).
     */
    availableFrom: string | null;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  // The room image that failed to load (keyed by URL, so a new room retries).
  const [failedHeroImage, setFailedHeroImage] = useState<string | null>(null);
  const idFileInputRef = useRef<HTMLInputElement>(null);
  const incomeFileInputRef = useRef<HTMLInputElement>(null);
  const permitFileInputRef = useRef<HTMLInputElement>(null);
  const documentsSectionRef = useRef<HTMLDivElement>(null);

  // Cinematic hero parallax (matches the Concept hero)
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], ["0%", "10%"]);
  const heroContentOpacity = useTransform(scrollY, [0, 460], [1, 0]);

  // The stored file_url is the public URL from upload time; the passport
  // bucket is going private (COL-436), so the document is signed on click.
  const viewExistingDocument = async (fileUrl: string) => {
    if (!(await openTenantDocument(fileUrl))) {
      sonnerToast.error(
        language === "de"
          ? "Dokument konnte nicht geöffnet werden."
          : "Could not open the document."
      );
    }
  };

  const getT = (key: string): string => {
    // In German, `t()` silently falls back to the English CMS value when the
    // German translation is missing (common right after a feature ships new
    // keys). That leaks English strings onto the German page. Read the raw
    // German value straight from the cache instead: if there's no real German
    // translation, use the hardcoded German fallback below rather than English.
    if (language === "de") {
      const deData = queryClient.getQueryData<{
        translations: Record<string, string>;
      }>(["translations", "de"]);
      const deValue = deData?.translations?.[key]?.trim();
      if (deValue && deValue !== key) return deValue;
      return BOOKING_FALLBACKS.de[key] ?? BOOKING_FALLBACKS.en[key] ?? "";
    }

    const fromT = t(key)?.trim();
    // Fix incomplete placeholder from CMS: "Tell us a bit about yourself, your lifestyle, and why you"
    if (
      key === "booking.descriptionPlaceholder" &&
      fromT &&
      /why you\s*$/i.test(fromT) &&
      !fromT.includes("like to live") &&
      !fromT.includes("wohnen möchten")
    ) {
      return (
        BOOKING_FALLBACKS[language]?.[key] ?? BOOKING_FALLBACKS.en[key] ?? ""
      );
    }
    // Fallback if translation is missing or returns the key itself
    if (fromT && fromT !== key) return fromT;
    return (
      BOOKING_FALLBACKS[language]?.[key] ?? BOOKING_FALLBACKS.en[key] ?? ""
    );
  };

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    // COL-530: shown on the public room page as a flatmate's gender, which is
    // why it is asked here rather than left to the tenant's settings — the
    // flat a new tenant joins is described to the next applicant.
    gender: "",
    // COL-618: ISO 3166-1 alpha-2 code. Decides whether a residence permit is
    // required, and is saved on the profile.
    nationality: "",
    description: "",
    moveInDate: "",
    toDate: "",
    // COL-618: null until answered — "never asked" must stay distinguishable
    // from a real "No", and the form insists on an answer.
    jobcenterPaysRent: null as boolean | null,
  });
  // bookings.name, profiles.name and the emails all carry one name.
  const fullName =
    `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim();
  const permitRequired = requiresResidencePermit(formData.nationality);
  const [idDocument, setIdDocument] = useState<File | null>(null);
  const [incomeDocument, setIncomeDocument] = useState<File | null>(null);
  const [permitDocument, setPermitDocument] = useState<File | null>(null);
  const [existingPermitDocument, setExistingPermitDocument] = useState<
    any | null
  >(null);
  const [useExistingPermitDocument, setUseExistingPermitDocument] =
    useState(true);
  const [existingIdDocument, setExistingIdDocument] = useState<any | null>(
    null
  );
  const [existingIncomeDocument, setExistingIncomeDocument] = useState<
    any | null
  >(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [useExistingIdDocument, setUseExistingIdDocument] = useState(true);
  const [useExistingIncomeDocument, setUseExistingIncomeDocument] =
    useState(true);
  const [moveInDatePopoverOpen, setMoveInDatePopoverOpen] = useState(false);
  const [toDatePopoverOpen, setToDatePopoverOpen] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  // A room link shared from the referral dashboard carries the referrer's code
  // (`?ref=Coliviq-123456`, see roomApplyUrl). Seed the field with it so the
  // friend does not have to retype it; ReferralCodeField validates the seeded
  // code exactly as it would a typed one, and the server re-checks on submit.
  const [referral, setReferral] = useState<ReferralCodeState>(() => {
    const seeded = normaliseReferralCode(
      searchParams.get(REFERRAL_URL_PARAM) ?? ""
    );
    return seeded
      ? { code: seeded, valid: null, referrerName: null }
      : EMPTY_REFERRAL_CODE_STATE;
  });

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setFormData((prev) => ({ ...prev, email: user.email || "" }));
    }
  }, [user]);

  // A returning applicant already told us their nationality; start from it
  // rather than asking again. Never overwrites what they have already picked.
  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void supabase
      .from("profiles")
      .select("nationality")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        const code = normalizeNationality(data?.nationality);
        if (!cancelled && code) {
          setFormData((prev) =>
            prev.nationality ? prev : { ...prev, nationality: code }
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // Fetch existing documents for logged-in user
  useEffect(() => {
    const fetchDocuments = async () => {
      if (!user?.id) {
        setExistingIdDocument(null);
        setExistingIncomeDocument(null);
        setExistingPermitDocument(null);
        return;
      }

      setDocumentsLoading(true);
      try {
        const [idResult, incomeResult, permitResult] = await Promise.all([
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("user_id", user.id)
            .eq("document_type", "id_passport")
            .order("created_at", { ascending: false })
            .limit(1),
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("user_id", user.id)
            .eq("document_type", "proof_of_income")
            .order("created_at", { ascending: false })
            .limit(1),
          supabase
            .from("tenant_documents")
            .select("*")
            .eq("user_id", user.id)
            .eq("document_type", "residence_permit")
            .order("created_at", { ascending: false })
            .limit(1),
        ]);

        if (idResult.error) throw idResult.error;
        if (incomeResult.error) throw incomeResult.error;
        if (permitResult.error) throw permitResult.error;

        const idDoc = idResult.data?.[0] || null;
        const incomeDoc = incomeResult.data?.[0] || null;
        const permitDoc = permitResult.data?.[0] || null;
        setExistingIdDocument(idDoc);
        setExistingIncomeDocument(incomeDoc);
        setExistingPermitDocument(permitDoc);
        setUseExistingIdDocument(!!idDoc);
        setUseExistingIncomeDocument(!!incomeDoc);
        setUseExistingPermitDocument(!!permitDoc);
      } catch (error) {
        console.error("Error fetching tenant documents:", error);
      } finally {
        setDocumentsLoading(false);
      }
    };

    fetchDocuments();
  }, [user?.id]);

  // Fetch room data - either by roomId or by propertyId
  useEffect(() => {
    const fetchRoomData = async () => {
      try {
        let actualRoomId: number | null = null;
        let roomData: any = null;
        let propertyData: any = null;

        if (roomIdParam) {
          // If roomId is provided, fetch the room directly
          actualRoomId = Number(roomIdParam);
          const { data: room, error: roomError } = await supabase
            .from("rooms")
            .select("*, properties(*)")
            .eq("id", actualRoomId)
            .single();

          if (roomError) throw roomError;
          roomData = room;
          propertyData = room.properties;
        } else if (propertyIdParam) {
          // If propertyId is provided, get the first available room for that property
          const propertyId = Number(propertyIdParam);

          if (isNaN(propertyId)) {
            throw new Error("Invalid property ID");
          }

          // First, fetch the property
          const { data: property, error: propertyError } = await supabase
            .from("properties")
            .select("*")
            .eq("id", propertyId)
            .single();

          if (propertyError) {
            console.error("Property fetch error:", propertyError);
            if (propertyError.code === "PGRST116") {
              throw new Error(
                `Property with ID ${propertyId} not found. The property may not exist in the database yet.`
              );
            }
            throw propertyError;
          }

          if (!property) {
            throw new Error(`Property with ID ${propertyId} not found`);
          }

          propertyData = property;

          // Then, get the first available room for this property
          const { data: rooms, error: roomsError } = await supabase
            .from("rooms")
            .select("*")
            .eq("property_id", propertyId)
            .eq("available", true)
            .limit(1);

          if (roomsError) throw roomsError;

          if (rooms && rooms.length > 0) {
            roomData = rooms[0];
            actualRoomId = rooms[0].id;
          } else {
            // If no room exists, try to get any room (even if not available)
            const { data: anyRooms, error: anyRoomsError } = await supabase
              .from("rooms")
              .select("*")
              .eq("property_id", propertyId)
              .limit(1);

            if (!anyRoomsError && anyRooms && anyRooms.length > 0) {
              roomData = anyRooms[0];
              actualRoomId = anyRooms[0].id;
            } else {
              // No rooms exist for this property - show error
              throw new Error(
                t(
                  "booking.noRoomsAvailable",
                  "No rooms available for this property. Please contact support or try another property."
                )
              );
            }
          }
        } else {
          throw new Error(
            t(
              "booking.missingRoomOrProperty",
              "Either roomId or propertyId must be provided"
            )
          );
        }

        if (actualRoomId && roomData && propertyData) {
          setRoomId(actualRoomId);

          // If user is logged in, check if they have already applied for this room
          if (user?.id) {
            const { data: existingBooking } = await supabase
              .from("bookings")
              .select("id")
              .eq("user_id", user.id)
              .eq("room_id", actualRoomId)
              .maybeSingle();
            if (existingBooking) {
              sonnerToast.info(
                t(
                  "booking.alreadyApplied",
                  "You have already applied for this room."
                )
              );
              navigate(`/property/${actualRoomId}`, { replace: true });
              return;
            }
          }

          // Construct image URL from propertyData.image_url
          let imageUrl = room1; // fallback
          if (propertyData.image_url) {
            if (propertyData.image_url.startsWith("http")) {
              imageUrl = propertyData.image_url;
            } else if (propertyData.image_url.startsWith("f/")) {
              imageUrl = `https://a.storyblok.com/${propertyData.image_url}`;
            } else {
              imageUrl = `https://a.storyblok.com/f/${propertyData.image_url}`;
            }
          }

          setRoom({
            id: actualRoomId,
            title: roomData.title || propertyData.title || "Room",
            address: propertyData.address || "Address not available",
            price: Number(roomData.price) || 850,
            baseDeposit:
              Number(roomData.deposit_amount) > 0
                ? Number(roomData.deposit_amount)
                : null,
            image: imageUrl,
            heroImage: roomHeroImage(roomData.images),
            availableFrom: getListingAvailability({
              available: roomData.available,
              available_from: roomData.available_from,
              next_available_date: roomData.next_available_date,
            }).nextAvailableDate,
          });
        }
      } catch (error: any) {
        console.error("Error fetching room data:", error);
        const errorMessage =
          error.message ||
          t("booking.loadRoomError", "Failed to load room information");
        toast({
          title: t("booking.errorTitle", "Error"),
          description: errorMessage,
          variant: "destructive",
        });
        // Don't set fallback room data - let the UI show the error state
        setRoom(null);
        setRoomId(null);
      } finally {
        setLoading(false);
      }
    };

    if (roomIdParam || propertyIdParam) {
      fetchRoomData();
    } else {
      setLoading(false);
      toast({
        title: t("booking.errorTitle", "Error"),
        description: t("booking.missingIds", "Property or Room ID is missing"),
        variant: "destructive",
      });
    }
  }, [roomIdParam, propertyIdParam, user?.id, navigate, toast, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!roomId || !room) {
      toast({
        title: t("booking.errorTitle", "Error"),
        description: t(
          "booking.roomUnavailable",
          "Room information is not available. Please go back and try again, or contact support if the problem persists."
        ),
        variant: "destructive",
      });
      return;
    }

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      toast({
        title: getT("booking.nameRequiredTitle"),
        description: getT("booking.nameRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    if (!formData.phone.trim()) {
      toast({
        title: t("booking.phoneRequiredTitle", "Phone number required"),
        description: t(
          "booking.phoneRequiredDescription",
          "Please provide a phone number so we can contact you about your application."
        ),
        variant: "destructive",
      });
      return;
    }

    if (!formData.gender) {
      toast({
        title: t("booking.genderRequiredTitle", "Gender required"),
        description: t(
          "booking.genderRequiredDescription",
          "Please select a gender — it is shown to future flatmates on the room page."
        ),
        variant: "destructive",
      });
      return;
    }

    if (!formData.nationality) {
      toast({
        title: getT("booking.nationalityRequiredTitle"),
        description: getT("booking.nationalityRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    if (formData.jobcenterPaysRent === null) {
      toast({
        title: getT("booking.jobcenterRequiredTitle"),
        description: getT("booking.jobcenterRequiredDescription"),
        variant: "destructive",
      });
      return;
    }

    const hasIdDocument =
      !!idDocument || (useExistingIdDocument && !!existingIdDocument);
    const hasIncomeDocument =
      !!incomeDocument ||
      (useExistingIncomeDocument && !!existingIncomeDocument);
    // Only nationals of a state outside the EU, EEA and Switzerland owe one.
    const hasPermitDocument =
      !permitRequired ||
      !!permitDocument ||
      (useExistingPermitDocument && !!existingPermitDocument);

    if (!hasIdDocument || !hasIncomeDocument || !hasPermitDocument) {
      const message =
        hasIdDocument && hasIncomeDocument
          ? getT("booking.residencePermitRequired")
          : t(
              "booking.documentsRequired",
              "Please upload your ID and salary statement to continue."
            );
      setDocumentsError(message);
      documentsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      sonnerToast.error(
        t("booking.documentsRequiredPrefix", "Documents required:") +
          " " +
          message
      );
      return;
    }
    setDocumentsError(null);

    // Validate dates up front so both the logged-in and new-account paths
    // fail fast before we create anything.
    if (!formData.moveInDate || !formData.toDate) {
      toast({
        title: t("booking.datesRequiredTitle", "Dates required"),
        description:
          getT("booking.dateRequired") ||
          "Preferred Move-in Date and To Date are required.",
        variant: "destructive",
      });
      return;
    }
    // The room is still occupied until its confirmed move-out date. The
    // calendar already greys those days out; this is the same rule for a date
    // that arrived any other way.
    if (
      room &&
      !moveInAllowsBooking(
        formData.moveInDate,
        !room.availableFrom,
        room.availableFrom
      )
    ) {
      toast({
        title: getT("booking.toDateInvalid") || "Invalid date",
        description: getT("booking.moveInBeforeAvailable").replace(
          "{date}",
          formatNextAvailableDate(
            room.availableFrom ?? "",
            language === "de" ? "de" : "en"
          )
        ),
        variant: "destructive",
      });
      return;
    }
    if (formData.toDate < getMinToDateAfterSixMonths(formData.moveInDate)) {
      toast({
        title: getT("booking.toDateInvalid") || "Invalid date",
        description:
          getT("booking.toDateMinError") ||
          "To Date must be at least 6 months after the Preferred Move-in Date.",
        variant: "destructive",
      });
      return;
    }

    // COL-246: a logged-out applicant creates their CoLiviq account straight
    // from the application. The account + booking + documents are persisted
    // server-side (create-application-account), then they confirm their email.
    if (!user) {
      if (
        !idDocument ||
        !incomeDocument ||
        (permitRequired && !permitDocument)
      ) {
        sonnerToast.error(
          idDocument && incomeDocument
            ? getT("booking.residencePermitRequired")
            : t(
                "booking.accountDocumentsRequired",
                "Please choose your ID and income documents so we can create your account."
              )
        );
        return;
      }
      setAccountError(null);
      setAccountDialogOpen(true);
      return;
    }

    // Prevent duplicate application for the same room (logged-in users)
    const { data: existingBooking } = await supabase
      .from("bookings")
      .select("id")
      .eq("user_id", user.id)
      .eq("room_id", roomId)
      .maybeSingle();
    if (existingBooking) {
      sonnerToast.info(
        t("booking.alreadyApplied", "You have already applied for this room.")
      );
      navigate(`/property/${roomId}`, { replace: true });
      return;
    }

    setIsSubmitting(true);

    try {
      const uploadDocument = async ({
        file,
        documentType,
        title,
        bookingId,
      }: {
        file: File;
        documentType: "id_passport" | "residence_permit" | "proof_of_income";
        title: string;
        bookingId: number;
      }) => {
        const timestamp = Date.now();
        const sanitizedName = file.name
          .trim()
          .replace(/\s+/g, "-")
          .replace(/[^a-zA-Z0-9._-]/g, "-")
          .replace(/-+/g, "-")
          .replace(/^[-.]+|[-.]+$/g, "");
        const filePath = `${user.id}/booking-${bookingId}/${documentType}-${timestamp}-${sanitizedName}`;

        const { error: uploadError } = await supabase.storage
          .from("tenant-documents")
          .upload(filePath, file, {
            contentType: file.type || "application/octet-stream",
            upsert: false,
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from("tenant-documents")
          .getPublicUrl(filePath);

        const { error: documentError } = await supabase
          .from("tenant_documents")
          .insert(
            operatorStamped<"tenant_documents">({
              user_id: user.id,
              booking_id: bookingId,
              document_type: documentType,
              title,
              file_url: urlData.publicUrl,
              file_name: file.name,
              file_size: file.size,
              mime_type: file.type || null,
              status: "pending",
              uploaded_by: user.id,
            })
          );

        if (documentError) throw documentError;
      };

      // Validate To Date is at least 6 months after From Date (Preferred Move-in Date)
      const fromDate = formData.moveInDate;
      const toDate = formData.toDate;
      if (!fromDate || !toDate) {
        throw new Error(
          getT("booking.dateRequired") ||
            "Preferred Move-in Date and To Date are required."
        );
      }
      const minToDateStr = getMinToDateAfterSixMonths(fromDate);
      if (toDate < minToDateStr) {
        throw new Error(
          getT("booking.toDateMinError") ||
            "To Date must be at least 6 months after the Preferred Move-in Date."
        );
      }

      // From Date and To Date are saved only in the bookings table (no other table).
      const startDate = fromDate; // From Date → start_date
      const endDate = toDate; // To Date → end_date

      const { data: booking, error: bookingError } = await supabase
        .from("bookings")
        .insert([
          operatorStamped<"bookings">({
            user_id: user?.id || null,
            room_id: Number(roomId),
            status: "pending",
            name: fullName,
            email: formData.email,
            phone: formData.phone || null,
            jobcenter_pays_rent: formData.jobcenterPaysRent,
            description: formData.description,
            move_in_date: formData.moveInDate || null,
            start_date: startDate, // From Date (only in bookings table)
            end_date: endDate, // To Date (only in bookings table)
            monthly_rent: Number(room.price),
            deposit_amount: applicationDepositEUR(room),
          }),
        ])
        .select()
        .single();

      if (bookingError) {
        throw bookingError;
      }

      // Attach the referral, if one was entered. Deliberately best-effort and
      // AFTER the booking exists: the application is the thing that must not be
      // lost. attach_referral_to_booking answers {ok:false, reason} rather than
      // raising, so a stale or mistyped code costs the applicant nothing.
      //
      // A rejection is SHOWN, not just logged. The code passed the live check
      // while they were typing, so a failure here means something changed in
      // between — and the applicant is the only person on screen who can still
      // do anything about it. Silently warning to the console is how a referrer
      // loses EUR 100 without anyone finding out.
      if (referral.code) {
        let attachWarning: string | null = null;
        try {
          const { data, error } = await supabase.rpc(
            "attach_referral_to_booking",
            { _booking_id: booking.id, _code: referral.code }
          );
          if (error) throw new Error(error.message);
          // jsonb: { ok, reason }, or null.
          const attached = data as { ok?: boolean; reason?: string } | null;
          if (!attached?.ok) {
            console.warn(
              "Referral not attached:",
              attached?.reason ?? "unknown"
            );
            attachWarning = referralAttachWarning(
              attached?.reason ?? "unknown",
              language === "de" ? "de" : "en"
            );
          }
        } catch (referralErr) {
          console.warn("Referral attach failed:", referralErr);
          attachWarning = referralAttachWarning(
            "error",
            language === "de" ? "de" : "en"
          );
        }
        if (attachWarning) sonnerToast.warning(attachWarning);
      }

      // Phone, gender and nationality all live on the profile, not the booking:
      // the room page reads gender from there, and nationality is a fact about
      // the person that a later application reuses. One patch so a missing
      // phone does not drop the gender. NOTE: there is no tenant-facing editor
      // for gender — what is chosen here is what the public page shows until an
      // admin changes it from the tenant panel (COL-530 scope, Neel
      // 2026-10-08). Nationality is likewise corrected by an admin.
      const profilePatch: {
        phone?: string;
        gender?: string;
        nationality?: string;
        name?: string;
        full_name?: string;
      } = {};
      if (formData.phone?.trim()) profilePatch.phone = formData.phone.trim();
      if (formData.gender) profilePatch.gender = formData.gender;
      if (formData.nationality) profilePatch.nationality = formData.nationality;
      // The name too, but only to fill a gap (see profileNameFromApplication).
      // Admin screens show profiles.full_name before bookings.name, so a last
      // name left on the booking alone is never seen. Both columns are written:
      // the sync trigger only mirrors one into the other while the other is
      // empty. An unreadable profile means "do not guess", not "overwrite".
      if (user?.id) {
        const { data: held, error: heldError } = await supabase
          .from("profiles")
          .select("name, full_name")
          .eq("id", user.id)
          .maybeSingle();
        const profileName = heldError
          ? null
          : profileNameFromApplication(held, fullName);
        if (profileName) {
          profilePatch.name = profileName;
          profilePatch.full_name = profileName;
        }
      }
      if (user?.id && Object.keys(profilePatch).length > 0) {
        const { error: profileSyncError } = await supabase
          .from("profiles")
          .update(profilePatch)
          .eq("id", user.id);
        if (profileSyncError) {
          console.warn("Profile sync failed:", profileSyncError);
        }
      }

      const uploads: Promise<void>[] = [];

      if (idDocument) {
        uploads.push(
          uploadDocument({
            file: idDocument,
            documentType: "id_passport",
            title: "Government ID",
            bookingId: booking.id,
          })
        );
      }

      if (permitRequired && permitDocument) {
        uploads.push(
          uploadDocument({
            file: permitDocument,
            documentType: "residence_permit",
            title: "Residence permit",
            bookingId: booking.id,
          })
        );
      }

      if (incomeDocument) {
        uploads.push(
          uploadDocument({
            file: incomeDocument,
            documentType: "proof_of_income",
            title: "Salary Statement",
            bookingId: booking.id,
          })
        );
      }

      if (uploads.length > 0) {
        await Promise.all(uploads);
      }

      // Send email notification via edge function using Supabase client
      try {
        console.log("Calling edge function: send-booking-email");
        console.log("Request payload:", {
          bookingId: booking.id,
          name: fullName,
          email: formData.email,
          language: language, // Include language in log for debugging
        });

        const { data: emailData, error: emailError } =
          await supabase.functions.invoke("send-booking-email", {
            body: {
              bookingId: booking.id,
              name: fullName,
              email: formData.email,
              phone: formData.phone,
              description: formData.description,
              roomTitle: room?.title || "Room",
              roomAddress: room?.address || "Address not available",
              roomPrice: room?.price || 0,
              moveInDate: formData.moveInDate,
              nationality: formData.nationality,
              jobcenterPaysRent: formData.jobcenterPaysRent,
              language: language, // Pass user's selected language
            },
          });

        if (emailError) {
          console.error(
            "Email sending failed, but booking was created:",
            emailError
          );
        } else {
          console.log("✅ Email sent successfully:", emailData);
        }
      } catch (emailError: any) {
        console.error("Error sending email:", emailError);
        console.error("Error details:", emailError.message);
        // Don't fail the booking if email fails
      }

      posthog.capture("booking submitted", {
        booking_id: booking.id,
        room_id: Number(roomId),
        room_title: room?.title,
        city: room?.address,
        monthly_rent: Number(room?.price),
        move_in_date: formData.moveInDate,
      });

      toast({
        title:
          BOOKING_FALLBACKS[language]?.["booking.successTitle"] ??
          BOOKING_FALLBACKS.en["booking.successTitle"] ??
          t("booking.successTitle"),
        description:
          BOOKING_FALLBACKS[language]?.["booking.success"] ??
          BOOKING_FALLBACKS.en["booking.success"] ??
          t("booking.success"),
      });

      // Redirect to properties page after successful submission
      setTimeout(() => {
        navigate("/rooms");
      }, 2000);
    } catch (error: any) {
      console.error("Error submitting booking:", error);
      // Daily application cap (DB trigger raises SQLSTATE 'CV429'). The CV429
      // code is the reliable signal; the message regex is only a belt-and-
      // suspenders fallback in case the SQLSTATE is lost in transit.
      const isDailyLimit =
        error?.code === "CV429" ||
        /limit of 3 applications per day/i.test(error?.message || "");
      const fb = (key: string) =>
        BOOKING_FALLBACKS[language]?.[key] ?? BOOKING_FALLBACKS.en[key] ?? "";
      toast({
        title: isDailyLimit
          ? fb("booking.dailyLimitTitle")
          : fb("booking.submitErrorTitle"),
        description: isDailyLimit
          ? fb("booking.dailyLimit")
          : error.message || fb("booking.submitError"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // COL-246: create the CoLiviq account + booking + documents in one
  // server-side call (no session exists yet), then prompt the applicant to
  // confirm their email. Invoked from the CompleteAccountDialog.
  const submitViaNewAccount = async (password: string) => {
    if (!roomId || !room) return;
    if (!idDocument || !incomeDocument) {
      setAccountError(
        t(
          "booking.accountDocumentsRequiredShort",
          "Please choose your ID and income documents."
        )
      );
      return;
    }
    if (permitRequired && !permitDocument) {
      setAccountError(getT("booking.residencePermitRequired"));
      return;
    }
    setAccountSubmitting(true);
    setAccountError(null);
    try {
      const form = new FormData();
      form.append(
        "payload",
        JSON.stringify({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email,
          phone: formData.phone,
          // Required on this form and shown publicly on the room page, so it
          // has to travel with account creation: a logged-out applicant never
          // reaches the profile patch in handleSubmit.
          gender: formData.gender,
          // Both required on this form and sent with account creation: a
          // logged-out applicant never reaches the profile patch in
          // handleSubmit, and the server enforces the permit rule from the
          // nationality it receives here.
          nationality: formData.nationality,
          jobcenterPaysRent: formData.jobcenterPaysRent,
          password,
          description: formData.description,
          roomId: Number(roomId),
          moveInDate: formData.moveInDate,
          startDate: formData.moveInDate,
          endDate: formData.toDate,
          language,
          referralCode: referral.code || null,
        })
      );
      form.append("id_document", idDocument);
      form.append("income_document", incomeDocument);
      if (permitRequired && permitDocument) {
        form.append("residence_permit_document", permitDocument);
      }

      const { data, error } = await supabase.functions.invoke(
        "create-application-account",
        { body: form }
      );

      // supabase-js surfaces non-2xx as a FunctionsHttpError; pull the JSON
      // body so we can show the specific reason (e.g. account already exists).
      if (error) {
        let message = t(
          "booking.accountCreateError",
          "Could not create your account. Please try again."
        );
        let code = "";
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") {
            const body = await ctx.json();
            message = body?.message || message;
            code = body?.error || "";
          }
        } catch {
          // keep default message
        }
        if (code === "account_exists") {
          setAccountDialogOpen(false);
          sonnerToast.info(message);
          setAuthModalOpen(true);
          return;
        }
        // Daily application cap enforced by the edge function (HTTP 429).
        if (code === "daily_limit") {
          setAccountError(
            BOOKING_FALLBACKS[language]?.["booking.dailyLimit"] ??
              BOOKING_FALLBACKS.en["booking.dailyLimit"] ??
              message
          );
          return;
        }
        setAccountError(message);
        return;
      }

      posthog.capture("booking submitted", {
        booking_id: data?.booking_id,
        room_id: Number(roomId),
        room_title: room?.title,
        monthly_rent: Number(room?.price),
        move_in_date: formData.moveInDate,
        via: "new_account",
      });

      setAccountDialogOpen(false);
      if (data?.warning === "account_created_documents_failed") {
        sonnerToast.warning(
          data?.message ||
            t(
              "booking.accountCreatedDocumentsFailed",
              "Account created, but your documents didn't upload. Confirm your email and re-upload from your dashboard."
            )
        );
      } else if (data?.warning === "account_created_permit_failed") {
        sonnerToast.warning(getT("booking.accountCreatedPermitFailed"));
      } else {
        sonnerToast.success(
          t(
            "booking.accountCreatedSuccess",
            "Account created! Check your email to confirm and unlock your dashboard."
          )
        );
      }
      // Independent of the documents warning above: both can be true, and a
      // rejected referral code is not something the documents message covers.
      const referralWarning = referralAttachWarning(
        data?.referral_warning,
        language === "de" ? "de" : "en"
      );
      if (referralWarning) sonnerToast.warning(referralWarning);
      // No session exists yet (the account is unconfirmed), so /dashboard would
      // bounce back to the home page. Send them to the confirm-email gate; the
      // email link then opens the dashboard directly. (COL-246, point 4)
      setTimeout(() => {
        navigate("/check-email", { state: { email: formData.email } });
      }, 1500);
    } catch (err: unknown) {
      setAccountError(
        err instanceof Error
          ? err.message
          : t("booking.genericError", "Something went wrong. Please try again.")
      );
    } finally {
      setAccountSubmitting(false);
    }
  };

  const fullMonthlyForPreview = room ? Number(room.price) : 0;
  const previewFirstMonthRent =
    room && formData.moveInDate
      ? computeFirstMonthRentEUR(
          fullMonthlyForPreview,
          formData.moveInDate,
          null
        )
      : fullMonthlyForPreview;
  const previewProRata =
    !!room &&
    !!formData.moveInDate &&
    Math.round(previewFirstMonthRent * 100) <
      Math.round(fullMonthlyForPreview * 100);

  // Full-bleed hero (~1920px wide); a failed room image falls back to apply-hero.
  const roomHeroSrc =
    room?.heroImage && room.heroImage !== failedHeroImage
      ? (getImageUrl(room.heroImage, { width: 1920 }) ?? room.heroImage)
      : null;
  const usingRoomHero = !!roomHeroSrc && !!room;
  const heroSrc = roomHeroSrc ?? applyHero;

  return (
    <>
      <div className="min-h-screen bg-sand pb-20">
        {/* Hero banner — full-bleed cinematic (matches the Concept hero) */}
        <section
          className="relative w-full overflow-hidden"
          style={{ height: "80vh", minHeight: 560 }}
        >
          {/* The hero is the room's own display image; the static apply-hero
              is only the fallback for rooms without one. Nothing renders
              while the room loads, so the generic image never flashes. */}
          {!loading && (
            <motion.img
              key={heroSrc}
              src={heroSrc}
              alt={
                usingRoomHero && room
                  ? room.title
                  : t("booking.heroImageAlt", "Apply for a room at coliviq")
              }
              style={{ y: heroY, scale: 1.12 }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              onError={() => setFailedHeroImage(room?.heroImage ?? null)}
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          )}
          {/* light editorial scrim (matches the Concept hero) */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(100deg, rgba(250,248,243,0.97) 0%, rgba(250,248,243,0.86) 34%, rgba(250,248,243,0.5) 62%, rgba(250,248,243,0.12) 100%)",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to top, rgba(250,248,243,0.92) 0%, transparent 45%)",
            }}
          />
          <motion.div
            style={{
              opacity: heroContentOpacity,
              paddingTop: "var(--hero-offset)",
            }}
            className="absolute inset-0 flex flex-col justify-center px-6 sm:px-16 lg:px-28"
          >
            <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.28em] text-primary">
              {language === "de" ? "Bewerbung" : "Application"}
            </p>
            <h1 className="font-display text-5xl font-semibold tracking-[-0.02em] text-heading sm:text-6xl lg:text-7xl">
              {t("booking.title")}
            </h1>
            <p className="mt-3 max-w-xl text-lg text-heading/65 sm:text-xl">
              {t("booking.subtitle")}
            </p>
          </motion.div>

          {/* scroll cue */}
          <motion.div
            style={{ opacity: heroContentOpacity }}
            className="absolute bottom-10 right-10 hidden flex-col items-center gap-2 md:flex"
          >
            <span
              className="text-[10px] uppercase tracking-widest"
              style={{
                color: "rgba(15,46,40,0.35)",
                writingMode: "vertical-rl",
              }}
            >
              scroll
            </span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.6, repeat: Infinity }}
              className="h-12 w-px"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(15,46,40,0.35), transparent)",
              }}
            />
          </motion.div>
        </section>

        <div className="container px-4 pt-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to={
                propertyIdParam
                  ? `/property/${propertyIdParam}`
                  : roomIdParam
                    ? `/property/${roomIdParam}`
                    : "/rooms"
              }
            >
              <Button variant="ghost" className="mb-6">
                <ArrowLeft className="mr-2 w-4 h-4" />
                {t("property.back")}
              </Button>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Room Information */}
              <div className="lg:col-span-1">
                <Card className="p-6 sticky top-24">
                  <h2 className="text-xl font-bold text-foreground mb-4">
                    {t("booking.roomInfo")}
                  </h2>
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
                      <p className="text-sm text-muted-foreground">
                        Loading room information...
                      </p>
                    </div>
                  ) : room ? (
                    <div className="space-y-4">
                      <img
                        // Booking summary thumb is ~400px wide in the right column.
                        src={
                          getImageUrl(room.image, { width: 600 }) ?? room.image
                        }
                        alt={room.title}
                        className="w-full h-48 object-cover rounded-lg mb-4"
                        loading="lazy"
                        onError={(e) => {
                          console.error("Image failed to load:", room.image);
                          e.currentTarget.src = room1; // Fallback to default image
                        }}
                      />
                      <div>
                        <h3 className="font-semibold text-foreground mb-2">
                          {room.title}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <MapPin className="w-4 h-4" />
                          {room.address}
                        </div>
                        <div className="flex items-center gap-2 text-lg font-bold text-foreground">
                          <Euro className="w-4 h-4" />
                          {room.price} {t("properties.perMonth")}
                        </div>
                      </div>

                      {/* Cost Breakdown */}
                      <div className="mt-6 pt-6 border-t border-border">
                        <h4 className="font-semibold text-foreground mb-4">
                          {t("booking.costOverview")}
                        </h4>
                        <div className="space-y-3 text-sm">
                          {/* Monthly rent (contract); first month pro-rata as a note when applicable */}
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-foreground">
                                {t("booking.monthlyRent")}
                              </div>
                              {previewProRata && (
                                <div className="text-xs text-muted-foreground mt-0.5 space-y-0.5">
                                  <div>
                                    {getT("booking.firstMonthProRatedRent")} = €
                                    {previewFirstMonthRent.toLocaleString(
                                      numberLocale,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </div>
                                  <div>
                                    {getT("booking.monthlyRentThereafter")} = €
                                    {fullMonthlyForPreview.toLocaleString(
                                      numberLocale,
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-foreground">
                                €
                                {fullMonthlyForPreview.toLocaleString(
                                  numberLocale,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Deposit */}
                          <div className="flex justify-between items-center">
                            <div>
                              <div className="font-medium text-foreground">
                                {t("booking.deposit")}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {t("booking.chargedAfterSigning")}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="font-bold text-foreground">
                                €
                                {applicationDepositEUR(room).toLocaleString(
                                  numberLocale,
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-destructive/10 rounded-lg border border-destructive/20">
                        <p className="text-sm text-destructive font-medium mb-2">
                          Room Not Available
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Unable to load room information. This property may not
                          have rooms set up yet, or the property doesn't exist
                          in the database.
                        </p>
                      </div>
                      <Link to="/rooms">
                        <Button variant="outline" className="w-full">
                          Browse Other Properties
                        </Button>
                      </Link>
                    </div>
                  )}
                </Card>
              </div>

              {/* Booking Form */}
              <Card className="lg:col-span-2 p-8">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {!user && (
                      <div className="rounded-lg border border-accent/50 bg-accent/10 p-4">
                        <p className="text-sm text-foreground">
                          {getT("booking.accountBannerText")}
                        </p>
                        <p className="mt-2 text-sm text-muted-foreground">
                          {getT("booking.alreadyHaveAccount")}{" "}
                          <button
                            type="button"
                            className="font-medium text-primary hover:underline"
                            onClick={() => setAuthModalOpen(true)}
                          >
                            {getT("booking.signIn")}
                          </button>
                        </p>
                      </div>
                    )}

                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label
                          htmlFor="firstName"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          {getT("booking.firstName")} *
                        </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          autoComplete="given-name"
                          value={formData.firstName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              firstName: e.target.value,
                            })
                          }
                          required
                          placeholder={getT("booking.firstNamePlaceholder")}
                        />
                      </div>
                      <div>
                        <Label
                          htmlFor="lastName"
                          className="block text-sm font-medium text-foreground mb-2"
                        >
                          {getT("booking.lastName")} *
                        </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          autoComplete="family-name"
                          value={formData.lastName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              lastName: e.target.value,
                            })
                          }
                          required
                          placeholder={getT("booking.lastNamePlaceholder")}
                        />
                      </div>
                    </div>

                    <div>
                      <Label
                        htmlFor="email"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {t("booking.email")} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        required
                        placeholder={t("booking.emailPlaceholder")}
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="phone"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {t("booking.phone")} *
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        required
                        placeholder={t("booking.phonePlaceholder")}
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="gender"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {t("booking.gender", "Gender")} *
                      </Label>
                      <Select
                        value={formData.gender}
                        onValueChange={(value) =>
                          setFormData({ ...formData, gender: value })
                        }
                      >
                        <SelectTrigger id="gender">
                          <SelectValue
                            placeholder={t(
                              "booking.genderPlaceholder",
                              "Please select"
                            )}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="female">
                            {t("booking.genderFemale", "Woman")}
                          </SelectItem>
                          <SelectItem value="male">
                            {t("booking.genderMale", "Man")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="mt-1.5 text-xs text-muted-foreground">
                        {t(
                          "booking.genderHint",
                          "Shown with your age on the room page, so people can see who lives in the flat. Your name is never shown."
                        )}
                      </p>
                    </div>

                    <div>
                      <Label
                        htmlFor="nationality"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {getT("booking.nationality")} *
                      </Label>
                      <NationalityCombobox
                        id="nationality"
                        value={formData.nationality}
                        onChange={(code) => {
                          setFormData((prev) => ({
                            ...prev,
                            nationality: code,
                          }));
                          // A permit chosen for a previous country no longer
                          // applies once the new one needs none.
                          if (!requiresResidencePermit(code)) {
                            setPermitDocument(null);
                            setDocumentsError(null);
                          }
                        }}
                        language={language}
                        placeholder={getT("booking.nationalityPlaceholder")}
                        searchPlaceholder={getT("booking.nationalitySearch")}
                        emptyText={getT("booking.nationalityEmpty")}
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="moveInDate"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {t("booking.moveInDate")} *
                      </Label>
                      <Popover
                        open={moveInDatePopoverOpen}
                        onOpenChange={setMoveInDatePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            id="moveInDate"
                            type="button"
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.moveInDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.moveInDate
                              ? format(
                                  parseISO(formData.moveInDate),
                                  "dd/MM/yyyy"
                                )
                              : getT("booking.moveInDatePlaceholder")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={
                              formData.moveInDate
                                ? parseISO(formData.moveInDate)
                                : undefined
                            }
                            onSelect={(date) => {
                              if (!date) return;
                              const value = format(date, "yyyy-MM-dd");
                              const toDateSixMonths =
                                getMinToDateAfterSixMonths(value);
                              setFormData((prev) => ({
                                ...prev,
                                moveInDate: value,
                                toDate: toDateSixMonths,
                              }));
                              setMoveInDatePopoverOpen(false);
                              setToDatePopoverOpen(true); // Open To Date calendar and keep it open to show 6-months date
                            }}
                            disabled={{
                              before:
                                room?.availableFrom &&
                                parseISO(room.availableFrom) > new Date()
                                  ? parseISO(room.availableFrom)
                                  : new Date(),
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      {room?.availableFrom && (
                        <p className="mt-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
                          {getT("booking.availableFrom")}{" "}
                          {formatNextAvailableDate(
                            room.availableFrom,
                            language === "de" ? "de" : "en"
                          )}
                        </p>
                      )}
                      <input
                        type="hidden"
                        name="moveInDate"
                        value={formData.moveInDate}
                        required
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="fromDate"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {getT("booking.fromDate")} *
                      </Label>
                      <Button
                        id="fromDate"
                        type="button"
                        variant="outline"
                        disabled
                        className={cn(
                          "w-full justify-start text-left font-normal bg-muted cursor-default",
                          !formData.moveInDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {formData.moveInDate
                          ? format(parseISO(formData.moveInDate), "dd/MM/yyyy")
                          : getT("booking.fromDatePlaceholder")}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">
                        {getT("booking.fromDateHint")}
                      </p>
                    </div>

                    <div>
                      <Label
                        htmlFor="toDate"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {getT("booking.toDate")} *
                      </Label>
                      <Popover
                        open={toDatePopoverOpen}
                        onOpenChange={setToDatePopoverOpen}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            id="toDate"
                            type="button"
                            variant="outline"
                            disabled={!formData.moveInDate}
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !formData.toDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.toDate
                              ? format(parseISO(formData.toDate), "dd/MM/yyyy")
                              : getT("booking.toDatePlaceholder")}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            key={
                              toDatePopoverOpen
                                ? formData.toDate ||
                                  (formData.moveInDate
                                    ? getMinToDateAfterSixMonths(
                                        formData.moveInDate
                                      )
                                    : "")
                                : "closed"
                            }
                            mode="single"
                            selected={
                              formData.toDate
                                ? parseISO(formData.toDate)
                                : undefined
                            }
                            defaultMonth={
                              formData.toDate
                                ? parseISO(formData.toDate)
                                : formData.moveInDate
                                  ? parseISO(
                                      getMinToDateAfterSixMonths(
                                        formData.moveInDate
                                      )
                                    )
                                  : undefined
                            }
                            onSelect={(date) => {
                              if (!date) return;
                              const toDateStr = format(date, "yyyy-MM-dd");
                              const fromDate = formData.moveInDate;
                              if (fromDate) {
                                const minTo =
                                  getMinToDateAfterSixMonths(fromDate);
                                if (toDateStr < minTo) {
                                  toast({
                                    title: getT("booking.toDateInvalid"),
                                    description: getT("booking.toDateMinError"),
                                    variant: "destructive",
                                  });
                                  return;
                                }
                              }
                              setFormData((prev) => ({
                                ...prev,
                                toDate: toDateStr,
                              }));
                              setToDatePopoverOpen(false);
                            }}
                            disabled={
                              formData.moveInDate
                                ? {
                                    before: parseISO(
                                      getMinToDateAfterSixMonths(
                                        formData.moveInDate
                                      )
                                    ),
                                  }
                                : undefined
                            }
                          />
                        </PopoverContent>
                      </Popover>
                      {formData.moveInDate && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {getT("booking.toDateHint")}
                        </p>
                      )}
                      <input
                        type="hidden"
                        name="toDate"
                        value={formData.toDate}
                        required
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="description"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {getT("booking.description")} *
                      </Label>
                      <Textarea
                        id="description"
                        value={formData.description}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            description: e.target.value,
                          })
                        }
                        required
                        placeholder={getT("booking.descriptionPlaceholder")}
                        rows={6}
                      />
                    </div>

                    <div>
                      <Label
                        id="jobcenter-label"
                        className="block text-sm font-medium text-foreground mb-2"
                      >
                        {getT("booking.jobcenterQuestion")} *
                      </Label>
                      <ToggleGroup
                        type="single"
                        variant="outline"
                        size="lg"
                        role="radiogroup"
                        aria-labelledby="jobcenter-label"
                        className="grid grid-cols-2 gap-3"
                        value={
                          formData.jobcenterPaysRent === null
                            ? ""
                            : formData.jobcenterPaysRent
                              ? "yes"
                              : "no"
                        }
                        onValueChange={(value) => {
                          // Radix reports "" when the chosen option is clicked
                          // again; an answer, once given, stays given.
                          if (!value) return;
                          setFormData((prev) => ({
                            ...prev,
                            jobcenterPaysRent: value === "yes",
                          }));
                        }}
                      >
                        {(["yes", "no"] as const).map((answer) => (
                          <ToggleGroupItem
                            key={answer}
                            value={answer}
                            className="w-full hover:bg-muted data-[state=on]:border-petrol data-[state=on]:bg-accent data-[state=on]:font-semibold data-[state=on]:text-petrol data-[state=on]:hover:bg-accent"
                          >
                            {getT(
                              answer === "yes"
                                ? "booking.jobcenterYes"
                                : "booking.jobcenterNo"
                            )}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                    </div>

                    <div ref={documentsSectionRef}>
                      <Label className="block text-sm font-medium text-foreground mb-2">
                        {getT("booking.requiredDocuments")} *
                      </Label>
                      {documentsError && (
                        <p
                          className="text-sm text-destructive font-medium mb-2"
                          role="alert"
                        >
                          {documentsError}
                        </p>
                      )}
                      <div className="space-y-4">
                        <ApplicationDocumentField
                          id="idDocument"
                          label={getT("booking.documentIdPassport")}
                          file={idDocument}
                          onFileChange={(file) => {
                            setIdDocument(file);
                            setDocumentsError(null);
                          }}
                          inputRef={idFileInputRef}
                          existing={existingIdDocument}
                          useExisting={useExistingIdDocument}
                          onUseExistingChange={(use) => {
                            setUseExistingIdDocument(use);
                            setDocumentsError(null);
                          }}
                          loading={documentsLoading}
                          translate={getT}
                          onFileRejected={setDocumentsError}
                          onView={(fileUrl) =>
                            void viewExistingDocument(fileUrl)
                          }
                        />
                        {permitRequired && (
                          <ApplicationDocumentField
                            id="permitDocument"
                            label={getT("booking.documentResidencePermit")}
                            hint={getT("booking.residencePermitHint")}
                            file={permitDocument}
                            onFileChange={(file) => {
                              setPermitDocument(file);
                              setDocumentsError(null);
                            }}
                            inputRef={permitFileInputRef}
                            existing={existingPermitDocument}
                            useExisting={useExistingPermitDocument}
                            onUseExistingChange={(use) => {
                              setUseExistingPermitDocument(use);
                              setDocumentsError(null);
                            }}
                            loading={documentsLoading}
                            translate={getT}
                            onFileRejected={setDocumentsError}
                            onView={(fileUrl) =>
                              void viewExistingDocument(fileUrl)
                            }
                          />
                        )}
                        <ApplicationDocumentField
                          id="incomeDocument"
                          label={getT("booking.documentSalaryProof")}
                          file={incomeDocument}
                          onFileChange={(file) => {
                            setIncomeDocument(file);
                            setDocumentsError(null);
                          }}
                          inputRef={incomeFileInputRef}
                          existing={existingIncomeDocument}
                          useExisting={useExistingIncomeDocument}
                          onUseExistingChange={(use) => {
                            setUseExistingIncomeDocument(use);
                            setDocumentsError(null);
                          }}
                          loading={documentsLoading}
                          translate={getT}
                          onFileRejected={setDocumentsError}
                          onView={(fileUrl) =>
                            void viewExistingDocument(fileUrl)
                          }
                        />
                      </div>
                    </div>

                    <ReferralCodeField
                      language={language === "de" ? "de" : "en"}
                      value={referral}
                      onChange={setReferral}
                      disabled={isSubmitting}
                    />

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full"
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t("booking.submitting")
                        : t("booking.submit")}
                    </Button>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
      <AuthModal open={authModalOpen} onOpenChange={setAuthModalOpen} />
      <CompleteAccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
        name={fullName}
        email={formData.email}
        loading={accountSubmitting}
        error={accountError}
        onSubmit={submitViaNewAccount}
      />
    </>
  );
};

export default BookingConfirmation;
